import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectIsPreinstall } from '@app/redux/auth';
import { resetSurveyValues } from '@app/redux/disconnect';

import DisconnectIntro from './components/DisconnectIntro';
import DisconnectRetention from './components/DisconnectRetention';
import DisconnectReview from './components/DisconnectReview';
import { useNavigationHandler } from './hooks';
import { DisconnectSteps, TotalSteps } from './types';

export type DisconnectPageNavigationProps = {
  handleIntroContinue: (step: DisconnectSteps) => void;
  handleReviewCancel: (step: DisconnectSteps) => void;
  step: DisconnectSteps;
  setStep: Dispatch<SetStateAction<DisconnectSteps>>;
  errorHandling: {
    setFormError: Dispatch<SetStateAction<string | undefined>>;
    setRadioError: Dispatch<SetStateAction<string | undefined>>;
    formError?: string;
    radioError?: string;
  };
};

const DisconnectPage = (): JSX.Element => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(DisconnectSteps.INTRO);

  const hasPreinstall = useSelector(selectIsPreinstall);

  const { handleIntroContinue, handleReviewCancel, formError, setFormError, radioError, setRadioError } =
    useNavigationHandler(setStep);

  useEffect(() => {
    return () => {
      dispatch(resetSurveyValues());
    };
  }, [dispatch]);

  const props: DisconnectPageNavigationProps = {
    handleIntroContinue,
    handleReviewCancel,
    step,
    setStep,
    errorHandling: {
      formError,
      setFormError,
      radioError,
      setRadioError,
    },
  };

  return hasPreinstall ? renderPreinstallSteps(props) : renderDefaultSteps(props);
};

const renderDefaultSteps = (props: DisconnectPageNavigationProps) => {
  const { handleIntroContinue, handleReviewCancel, step, setStep, errorHandling } = props;

  const handleContinue = () => handleIntroContinue(DisconnectSteps.RETENTION);
  const handleCancel = () => handleReviewCancel(DisconnectSteps.RETENTION);

  switch (step) {
    case DisconnectSteps.INTRO:
      return <DisconnectIntro handleContinue={handleContinue} errorHandling={errorHandling} />;
    case DisconnectSteps.RETENTION:
      return <DisconnectRetention setStep={setStep} />;
    case DisconnectSteps.REVIEW:
      return <DisconnectReview step={TotalSteps.DEFAULT} handleCancel={handleCancel} />;
    default:
      return <></>;
  }
};

const renderPreinstallSteps = (props: DisconnectPageNavigationProps) => {
  const { handleIntroContinue, handleReviewCancel, step, errorHandling } = props;

  const handleContinue = () => handleIntroContinue(DisconnectSteps.REVIEW);
  const handleCancel = () => handleReviewCancel(DisconnectSteps.INTRO);

  switch (step) {
    case DisconnectSteps.INTRO:
      return <DisconnectIntro handleContinue={handleContinue} errorHandling={errorHandling} />;
    case DisconnectSteps.REVIEW:
      return <DisconnectReview step={TotalSteps.PREINSTALL} handleCancel={handleCancel} />;
    default:
      return <></>;
  }
};

export default DisconnectPage;
